import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import AppAnalytics from '../analytics/AppAnalytics';

interface PromptPreset {
  id: string;
  title: string;
  prompt: string;
  category: string;
  exampleImage?: string;
}

interface BaseModel {
  id: string;
  name: string;
  description: string;
  exampleImage: string;
  type: string;
}

@customElement('generate-images-form')
export class GenerateImagesForm extends LitElement {
  @state()
  private selectedBaseModel = 'sd15';

  @state()
  private prompt = '';

  @state()
  private showPresets = false;

  @state()
  private selectedCategory = 'all';

  private presets: PromptPreset[] = [
    {
      id: 'portrait-cyber',
      title: 'Cyberpunk Portrait',
      prompt: 'professional portrait, cyberpunk style, neon lighting, futuristic clothing, detailed facial features, high contrast, vibrant colors',
      category: 'portrait',
      exampleImage: '/static/images/difflora/portrait_00070_.png'
    },
    {
      id: 'landscape-sci-fi',
      title: 'Sci-Fi Landscape',
      prompt: 'futuristic cityscape, towering skyscrapers, flying vehicles, holographic displays, neon signs, dramatic lighting, atmospheric perspective',
      category: 'landscape',
      exampleImage: '/static/images/difflora/cityscape_00001_.png'
    },
    {
      id: 'character-anime',
      title: 'Anime Character',
      prompt: 'anime style character, detailed eyes, expressive face, colorful hair, dynamic pose, studio lighting, high quality illustration',
      category: 'character',
      exampleImage: '/static/images/difflora/anime-character_00001_.png'
    },
    {
      id: 'product-minimal',
      title: 'Minimal Product Shot',
      prompt: 'product photography, minimalist composition, clean background, professional lighting, high detail, commercial quality',
      category: 'product',
      exampleImage: '/static/images/difflora/product-shot_00001_.png'
    },
    {
      id: 'art-abstract',
      title: 'Abstract Digital Art',
      prompt: 'abstract digital art, flowing geometric patterns, vibrant gradients, dynamic composition, modern aesthetic, high resolution',
      category: 'art',
      exampleImage: '/static/images/difflora/abstract-art_00001_.png'
    },
    {
      id: 'portrait-realistic',
      title: 'Photorealistic Portrait',
      prompt: 'photorealistic portrait, natural lighting, professional photography, detailed skin texture, expressive eyes, shallow depth of field',
      category: 'portrait',
      exampleImage: '/static/images/difflora/photorealistic-portrait_00065_.png'
    },
    {
      id: 'fantasy-creature',
      title: 'Fantasy Creature',
      prompt: 'fantasy creature, mythical being, detailed textures, magical atmosphere, epic composition, dramatic lighting, high fantasy style',
      category: 'character',
      exampleImage: '/static/images/difflora/fantasy-creature_00001_.png'
    },
    {
      id: 'nature-ethereal',
      title: 'Ethereal Nature',
      prompt: 'ethereal nature scene, mystical forest, soft lighting, magical atmosphere, detailed foliage, cinematic composition',
      category: 'landscape',
      exampleImage: '/static/images/difflora/ethereal_00001_.png'
    }
  ];

  private baseModels: BaseModel[] = [
    /*
    {
      id: 'sd15',
      name: 'Stable Diffusion 1.5',
      description: 'Versatile model great for general purpose image generation',
      exampleImage: '/static/images/difflora/character_00002_.png',
      type: 'General'
    },
    */
    {
      id: 'sdxl',
      name: 'Stable Diffusion XL',
      description: 'Higher resolution model with improved detail and quality',
      exampleImage: '/static/images/difflora/steampunk2_00005_.png',
      type: 'High Quality'
    },
    {
      id: 'realistic',
      name: 'Realistic Vision',
      description: 'Specialized for photorealistic human portraits and scenes',
      exampleImage: '/static/images/difflora/portrait_00063_.png',
      type: 'Photorealistic'
    },
    {
      id: 'anime',
      name: 'Anime Diffusion',
      description: 'Optimized for anime and manga style illustrations',
      exampleImage: '/static/images/difflora/anime2_00032_.png',
      type: 'Anime/Manga'
    },
    /*
    {
      id: 'artistic',
      name: 'Artistic Vision',
      description: 'Perfect for creative and artistic style generations',
      exampleImage: '/static/images/difflora/art_00002_.png',
      type: 'Artistic'
    }
    */
  ];

  static styles = css`
    :host {
      display: block;
    }

    .form-container {
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 1rem;
      padding: 2rem;
      transform: hover:scale-[1.02];
      transition: all 0.3s;
      max-width: 48rem;
      margin: 0 auto;
    }

    @media (min-width: 768px) {
      .form-container {
        padding: 3rem;
      }
    }

    .form-container:hover {
      transform: scale(1.02);
    }

    .form-section {
      margin-bottom: 2.5rem;
    }

    .label {
      display: block;
      font-size: 1.125rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      margin-bottom: 1rem;
    }

    .select, .textarea {
      width: 100%;
      padding: 1rem 1.5rem;
      border-radius: 0.75rem;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-color: rgba(26, 29, 41, 0.5);
      color: #e2e8f0;
      font-family: 'JetBrains Mono', monospace;
      transition: all 0.2s;
    }

    .select:focus, .textarea:focus {
      outline: none;
      border-color: #00d4ff;
      box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.2);
    }

    .textarea {
      height: 8rem;
      resize: none;
    }

    .textarea.negative {
      height: 6rem;
    }

    .pricing-section {
      border-radius: 1rem;
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      padding: 2rem;
      margin-bottom: 2.5rem;
      text-align: center;
    }

    .pricing-label {
      font-size: 0.875rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
      color: #00ff88;
      margin-bottom: 0.75rem;
    }

    .pricing-amount {
      font-size: 2.5rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      margin-bottom: 0.75rem;
    }

    .pricing-description {
      font-size: 0.875rem;
      color: rgba(226, 232, 240, 0.7);
      font-family: 'JetBrains Mono', monospace;
    }

    .submit-button {
      width: 100%;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-image: linear-gradient(to right, #a855f7, #ec4899);
      color: #0a0f1b;
      padding: 1.25rem;
      border-radius: 0.75rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      font-size: 1.25rem;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
    }

    .submit-button:hover {
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      transform: scale(1.05);
    }

    .presets-section {
      margin-bottom: 1rem;
    }

    .presets-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .presets-toggle {
      background: none;
      border: 1px solid rgba(0, 212, 255, 0.5);
      color: #00d4ff;
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .presets-toggle:hover {
      background: rgba(0, 212, 255, 0.1);
    }

    .presets-toggle.active {
      background: rgba(0, 212, 255, 0.2);
    }

    .presets-panel {
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 0.75rem;
      background: rgba(0, 212, 255, 0.05);
      padding: 1.5rem;
      margin-bottom: 1rem;
      max-height: 0;
      overflow: hidden;
      transition: all 0.3s;
    }

    .presets-panel.open {
      max-height: 600px;
    }

    .category-filters {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
    }

    .category-button {
      background: none;
      border: 1px solid rgba(0, 212, 255, 0.3);
      color: rgba(226, 232, 240, 0.7);
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.2s;
    }

    .category-button:hover {
      color: #00d4ff;
      border-color: rgba(0, 212, 255, 0.5);
    }

    .category-button.active {
      background: rgba(0, 212, 255, 0.2);
      color: #00d4ff;
      border-color: #00d4ff;
    }

    .presets-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1rem;
    }

    .preset-card {
      border: 1px solid rgba(0, 212, 255, 0.2);
      border-radius: 0.5rem;
      padding: 1rem;
      cursor: pointer;
      transition: all 0.2s;
      background: rgba(0, 212, 255, 0.02);
    }

    .preset-card:hover {
      border-color: rgba(0, 212, 255, 0.5);
      background: rgba(0, 212, 255, 0.05);
      transform: translateY(-2px);
    }

    .preset-image {
      width: 100%;
      height: 120px;
      border-radius: 0.5rem;
      object-fit: cover;
      margin-bottom: 0.75rem;
      background: linear-gradient(45deg, #0a0f1b, #1a1d29);
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(226, 232, 240, 0.5);
      font-size: 2rem;
    }

    .preset-title {
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      color: #00d4ff;
      font-size: 0.875rem;
      margin-bottom: 0.5rem;
    }

    .preset-prompt {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75rem;
      color: rgba(226, 232, 240, 0.7);
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .base-models-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .model-card {
      border: 2px solid rgba(0, 212, 255, 0.2);
      border-radius: 0.75rem;
      padding: 1rem;
      cursor: pointer;
      transition: all 0.2s;
      background: rgba(0, 212, 255, 0.02);
      position: relative;
    }

    .model-card:hover {
      border-color: rgba(0, 212, 255, 0.5);
      background: rgba(0, 212, 255, 0.05);
      transform: translateY(-2px);
    }

    .model-card.selected {
      border-color: #00d4ff;
      background: rgba(0, 212, 255, 0.1);
      box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
    }

    .model-image img {
      object-position: top;
    }

    .model-image {
      width: 100%;
      height: 240px;
      border-radius: 0.5rem;
      object-fit: cover;
      margin-bottom: 1rem;
      background: linear-gradient(45deg, #0a0f1b, #1a1d29);
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(226, 232, 240, 0.5);
      font-size: 2.5rem;
    }

    .model-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.5rem;
    }

    .model-name {
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      color: #00d4ff;
      font-size: 0.875rem;
      line-height: 1.2;
    }

    .model-type {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75rem;
      color: #00ff88;
      background: rgba(0, 255, 136, 0.1);
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      white-space: nowrap;
    }

    .model-description {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.75rem;
      color: rgba(226, 232, 240, 0.7);
      line-height: 1.4;
    }

    .selected-indicator {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      color: #00d4ff;
      font-size: 1.25rem;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .model-card.selected .selected-indicator {
      opacity: 1;
    }

    .cyber-green { color: #00ff88; }
    .cyber-yellow { color: #ffd700; }
    .cyber-purple { color: #a855f7; }
    .cyber-pink { color: #ec4899; }
  `;

  private handleGenerateImages() {
    AppAnalytics.getPlausible()?.trackEvent('cta_clicked', {
      props: {
        page: 'generate-images',
        button: 'generate_images',
        location: 'main_form'
      }
    });

    const emailModal = document.querySelector('email-modal');
    if (emailModal) {
      (emailModal as any).source = 'generate_images_page';
      (emailModal as any).open = true;
      (emailModal as any).setAttribute('open', 'true');
      (emailModal as any).requestUpdate();
    }
  }

  private togglePresets() {
    this.showPresets = !this.showPresets;
    this.requestUpdate();
  }

  private selectCategory(category: string) {
    this.selectedCategory = category;
    this.requestUpdate();
  }

  private selectPreset(preset: PromptPreset) {
    this.prompt = preset.prompt;
    this.showPresets = false;

    // Track preset selection
    AppAnalytics.getPlausible()?.trackEvent('preset_selected', {
      props: {
        preset_id: preset.id,
        preset_title: preset.title,
        category: preset.category,
        location: 'generate_images_form'
      }
    });

    // Update the textarea value
    this.requestUpdate();
  }

  private handlePromptInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    this.prompt = target.value;
    this.requestUpdate();
  }

  private get filteredPresets() {
    if (this.selectedCategory === 'all') {
      return this.presets;
    }
    return this.presets.filter(preset => preset.category === this.selectedCategory);
  }

  private get categories() {
    const cats = ['all', ...new Set(this.presets.map(p => p.category))];
    return cats;
  }

  private selectBaseModel(modelId: string) {
    this.selectedBaseModel = modelId;
    this.requestUpdate();

    // Track base model selection
    AppAnalytics.getPlausible()?.trackEvent('base_model_selected', {
      props: {
        model_id: modelId,
        model_name: this.baseModels.find(m => m.id === modelId)?.name || modelId,
        location: 'generate_images_form'
      }
    });
  }

  render() {
    return html`
      <div class="form-container">
        <!-- Base Model Selection -->
        <div class="form-section">
          <label class="label">
            <span class="cyber-green">[</span>BASE_MODEL<span class="cyber-green">]</span>
          </label>
          <div class="base-models-grid">
            ${this.baseModels.map(model => html`
              <div
                class="model-card ${this.selectedBaseModel === model.id ? 'selected' : ''}"
                @click=${() => this.selectBaseModel(model.id)}>
                <div class="selected-indicator">✓</div>
                <div class="model-image">
                  <img src="${model.exampleImage}" alt="${model.name}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 0.5rem;"
                       @error=${(e: Event) => {
                         const target = e.target as HTMLImageElement;
                         target.style.display = 'none';
                         target.parentElement!.innerHTML = '<span>🤖</span>';
                       }} />
                </div>
                <div class="model-header">
                  <div class="model-name">${model.name}</div>
                  <div class="model-type">${model.type}</div>
                </div>
                <div class="model-description">${model.description}</div>
              </div>
            `)}
          </div>
        </div>

        <!-- LoRA Model Selection -->
        <div class="form-section">
          <label class="label">
            <span class="cyber-purple">[</span>LORA_MODEL<span class="cyber-purple">]</span> <span style="color: rgba(226, 232, 240, 0.5); font-size: 0.875rem;">(OPTIONAL)</span>
          </label>
          <select class="select">
            <option value="">NO_LORA_MODEL</option>
            <option value="my-character-v1">MY_CHARACTER_V1</option>
            <option value="portrait-style">PORTRAIT_STYLE</option>
            <option value="product-shots">PRODUCT_SHOTS</option>
          </select>
        </div>

        <!-- Prompt Input -->
        <div class="form-section">
          <div class="presets-header">
            <label class="label">
              <span class="cyber-purple">[</span>PROMPT<span class="cyber-purple">]</span>
            </label>
            <button
              class="presets-toggle ${this.showPresets ? 'active' : ''}"
              @click=${this.togglePresets}>
              <span>🎯</span>
              ${this.showPresets ? 'HIDE_PRESETS' : 'BROWSE_PRESETS'}
            </button>
          </div>

          <div class="presets-section">
            <div class="presets-panel ${this.showPresets ? 'open' : ''}">
              <div class="category-filters">
                ${this.categories.map(category => html`
                  <button
                    class="category-button ${this.selectedCategory === category ? 'active' : ''}"
                    @click=${() => this.selectCategory(category)}>
                    ${category.toUpperCase()}
                  </button>
                `)}
              </div>

              <div class="presets-grid">
                ${this.filteredPresets.map(preset => html`
                  <div class="preset-card" @click=${() => this.selectPreset(preset)}>
                    <!--<div class="preset-image">
                      ${preset.exampleImage ? html`
                        <img src="${preset.exampleImage}" alt="${preset.title}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 0.5rem;" />
                      ` : html`
                        <span>🎨</span>
                      `}
                    </div>-->
                    <div class="preset-title">${preset.title}</div>
                    <div class="preset-prompt">${preset.prompt}</div>
                  </div>
                `)}
              </div>
            </div>
          </div>

          <textarea
            class="textarea"
            .value=${this.prompt}
            @input=${this.handlePromptInput}
            placeholder="DESCRIBE_THE_IMAGE_YOU_WANT_TO_GENERATE..."></textarea>
        </div>

        <!-- Negative Prompt -->
        <div class="form-section">
          <label class="label">
            <span class="cyber-yellow">[</span>NEGATIVE_PROMPT<span class="cyber-yellow">]</span> <span style="color: rgba(226, 232, 240, 0.5); font-size: 0.875rem;">(OPTIONAL)</span>
          </label>
          <textarea class="textarea negative" placeholder="WHAT_YOU_DONT_WANT_IN_THE_IMAGE..."></textarea>
        </div>


        <!-- Pricing Display -->
        <div class="pricing-section">
          <div class="pricing-label">
            <span class="cyber-yellow">[</span>GENERATION_COST<span class="cyber-yellow">]</span>
          </div>
          <div class="pricing-amount">1 CREDIT</div>
          <div class="pricing-description">
            Per image generated
          </div>
        </div>

        <!-- Generate Button -->
        <button
          class="submit-button"
          @click=${this.handleGenerateImages}>
          <span>🎨</span>GENERATE_IMAGES_NOW
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'generate-images-form': GenerateImagesForm;
  }
}
